/* PCIS Health — Summary (route /supply/pcis-health/summary). Aggregate rollup for
   a from/to window (blank = last 60 minutes), optionally scoped to a courier/account.
   Confirmed /health/summary response shape: {from, to, totalOrders, failedOrders,
   successOrders, failureRatePct}. That's the ONLY data available here — no
   per-courier breakdown — so the page focuses on presenting those aggregates well:
   a gradient donut health gauge + status pill up top, interactive KPI cards below.

   Palette is intentionally NOT the stock green/red: teal = success, coral/rose =
   failed, indigo = total, plus an amber "degraded" band. */
import React, {useState} from 'react'
import {Box, Typography, Stack} from '@mui/material'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined'
import PageTitle from '../../../components/PageTitle'
import {PageHeader, TableSkeleton, LoadErrorPanel} from '../components'
import {useListPcisCourierAccountsQuery, usePcisHealthSummaryQuery} from '../../../redux/supplyApiSlice'
import PcisHealthFilterBar, {defaultFilters} from './PcisHealthFilterBar'

/* Refined palette — {main, soft(bg tint), g1→g2 gradient} */
const TEAL = {main: '#0EA5A4', soft: '#E6FBF8', g1: '#2DD4BF', g2: '#0EA5A4'}
const CORAL = {main: '#F43F5E', soft: '#FFECEF', g1: '#FB7185', g2: '#F43F5E'}
const INDIGO = {main: '#6366F1', soft: '#EEF0FE', g1: '#818CF8', g2: '#6366F1'}
const AMBER = {main: '#F59E0B', soft: '#FEF3E2', g1: '#FBBF24', g2: '#F59E0B'}

const pctOf = (n, total) => (total ? (n / total) * 100 : 0)

/* Healthy < 5% ≤ Degraded < 20% ≤ Critical */
const healthBand = (rate) =>
  rate >= 20 ? {label: 'Critical', c: CORAL} : rate >= 5 ? {label: 'Degraded', c: AMBER} : {label: 'Healthy', c: TEAL}

const fmtWindow = (v) => {
  if (!v) return null
  const d = new Date(v)
  return isNaN(d) ? v : d.toLocaleString('en-IN', {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'})
}

/* Donut gauge — teal success ring with a coral failed arc, gradient strokes,
   failure-rate + health in the centre. */
function HealthDonut({success, failed, rate, band}) {
  const total = success + failed
  const R = 70
  const C = 2 * Math.PI * R
  const failLen = (total ? failed / total : 0) * C

  return (
    <Box sx={{position: 'relative', width: 184, height: 184, flexShrink: 0}}>
      <Box component='svg' viewBox='0 0 184 184' sx={{width: '100%', height: '100%', transform: 'rotate(-90deg)'}}>
        <defs>
          <linearGradient id='pcisTeal' x1='0%' y1='0%' x2='100%' y2='100%'>
            <stop offset='0%' stopColor={TEAL.g1} />
            <stop offset='100%' stopColor={TEAL.g2} />
          </linearGradient>
          <linearGradient id='pcisCoral' x1='0%' y1='0%' x2='100%' y2='100%'>
            <stop offset='0%' stopColor={CORAL.g1} />
            <stop offset='100%' stopColor={CORAL.g2} />
          </linearGradient>
        </defs>
        {/* success ring (teal gradient) */}
        <circle cx='92' cy='92' r={R} fill='none' stroke='url(#pcisTeal)' strokeWidth='16' opacity={total ? 1 : 0.15} strokeLinecap='round' />
        {/* failed arc on top (coral gradient) */}
        {total ? (
          <circle
            cx='92'
            cy='92'
            r={R}
            fill='none'
            stroke='url(#pcisCoral)'
            strokeWidth='16'
            strokeLinecap='round'
            strokeDasharray={`${failLen} ${C - failLen}`}
          />
        ) : null}
      </Box>
      <Box sx={{position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center'}}>
        <Typography sx={{fontSize: 34, fontWeight: 800, lineHeight: 1, color: band.c.main, letterSpacing: '-.02em'}}>
          {rate.toFixed(1)}
          <Box component='span' sx={{fontSize: 15, ml: 0.25}}>
            %
          </Box>
        </Typography>
        <Typography sx={{fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary', mt: 0.5}}>
          Failure rate
        </Typography>
      </Box>
    </Box>
  )
}

/* Interactive KPI card — gradient icon tile, big value, share bar, hover lift. */
function KpiCard({label, value, unit, sub, icon, palette, share}) {
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '16px',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        p: 2.25,
        backgroundImage: `linear-gradient(135deg, ${palette.soft} 0%, rgba(255,255,255,0) 60%)`,
        boxShadow: '0 1px 2px rgba(15,23,42,.04)',
        transition: 'transform .18s ease, box-shadow .18s ease',
        '&:hover': {transform: 'translateY(-3px)', boxShadow: `0 12px 26px ${palette.main}22`},
      }}
    >
      <Stack direction='row' alignItems='center' spacing={1.25} sx={{mb: 1.25}}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            flexShrink: 0,
            backgroundImage: `linear-gradient(135deg, ${palette.g1}, ${palette.g2})`,
            boxShadow: `0 4px 12px ${palette.main}44`,
            '& svg': {fontSize: 20},
          }}
        >
          {icon}
        </Box>
        <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary'}}>
          {label}
        </Typography>
      </Stack>

      <Typography sx={{fontSize: 30, fontWeight: 800, lineHeight: 1, letterSpacing: '-.02em', color: 'text.primary'}}>
        {value}
        {unit ? (
          <Box component='span' sx={{fontSize: 14, fontWeight: 700, color: palette.main, ml: 0.5}}>
            {unit}
          </Box>
        ) : null}
      </Typography>

      {share != null ? (
        <Box sx={{mt: 1.5}}>
          <Box sx={{height: 6, borderRadius: '999px', bgcolor: 'action.hover', overflow: 'hidden'}}>
            <Box sx={{height: '100%', width: `${share}%`, borderRadius: '999px', backgroundImage: `linear-gradient(90deg, ${palette.g1}, ${palette.g2})`, transition: 'width .35s ease'}} />
          </Box>
          {sub ? <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 0.75}}>{sub}</Typography> : null}
        </Box>
      ) : sub ? (
        <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 1.25}}>{sub}</Typography>
      ) : null}
    </Box>
  )
}

export default function PcisHealthSummary() {
  const [filters, setFilters] = useState(() => defaultFilters())
  const {data: accounts} = useListPcisCourierAccountsQuery()
  const {data: summary, isFetching, isError, error, refetch} = usePcisHealthSummaryQuery(filters)

  const total = summary?.totalOrders ?? 0
  const success = summary?.successOrders ?? 0
  const failed = summary?.failedOrders ?? 0
  const rate = summary?.failureRatePct ?? pctOf(failed, total)
  const band = healthBand(rate)
  const successShare = pctOf(success, total)
  const failedShare = pctOf(failed, total)
  const winFrom = fmtWindow(summary?.from)
  const winTo = fmtWindow(summary?.to)

  return (
    <>
      <PageTitle>PCIS — Summary</PageTitle>
      <PageHeader title='PCIS' sub='Aggregate order health for the Direct Integration (PCIS) courier accounts.' />

      <PcisHealthFilterBar filters={filters} onChange={setFilters} accounts={accounts} />

      {isFetching && !summary ? (
        <TableSkeleton kpis={4} rows={0} />
      ) : isError ? (
        <LoadErrorPanel message={error?.message || 'Could not load the summary.'} onRetry={refetch} />
      ) : summary ? (
        <>
          {/* Health hero */}
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              borderRadius: '18px',
              bgcolor: 'background.paper',
              backgroundImage: `linear-gradient(135deg, ${band.c.soft} 0%, rgba(255,255,255,0) 55%)`,
              boxShadow: '0 1px 2px rgba(15,23,42,.04)',
              p: {xs: 2.5, md: 3.5},
              mb: 2.5,
            }}
          >
            <Box sx={{position: 'absolute', left: 0, right: 0, top: 0, height: 4, backgroundImage: `linear-gradient(90deg, ${band.c.g1}, ${band.c.g2})`}} />
            <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, alignItems: {xs: 'stretch', md: 'center'}, gap: {xs: 2.5, md: 4.5}}}>
              <HealthDonut success={success} failed={failed} rate={rate} band={band} />

              <Box sx={{flex: 1, minWidth: 0}}>
                <Stack direction='row' alignItems='center' spacing={1.25} sx={{mb: 0.5}}>
                  <Typography sx={{fontSize: 18, fontWeight: 800, color: 'text.primary'}}>Placement Health</Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: '999px',
                      bgcolor: band.c.soft,
                      color: band.c.main,
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: band.c.main}} />
                    {band.label}
                  </Box>
                </Stack>
                <Typography sx={{fontSize: 12.5, color: 'text.secondary', mb: 2.25}}>
                  {winFrom && winTo ? `${winFrom} → ${winTo}` : 'Last 60 minutes'}
                </Typography>

                {/* segmented bar */}
                <Box sx={{display: 'flex', height: 12, borderRadius: '999px', overflow: 'hidden', bgcolor: 'action.hover', mb: 1.5}}>
                  <Box sx={{width: `${successShare}%`, backgroundImage: `linear-gradient(90deg, ${TEAL.g1}, ${TEAL.g2})`, transition: 'width .35s ease'}} />
                  <Box sx={{width: `${failedShare}%`, backgroundImage: `linear-gradient(90deg, ${CORAL.g1}, ${CORAL.g2})`, transition: 'width .35s ease'}} />
                </Box>

                <Stack direction={{xs: 'column', sm: 'row'}} spacing={{xs: 1, sm: 4}}>
                  <Stack direction='row' alignItems='center' spacing={1.25}>
                    <Box sx={{width: 10, height: 10, borderRadius: '3px', bgcolor: TEAL.main}} />
                    <Typography sx={{fontSize: 13, color: 'text.secondary', minWidth: 60}}>Success</Typography>
                    <Typography sx={{fontSize: 15, fontWeight: 700}}>{success.toLocaleString()}</Typography>
                    <Typography sx={{fontSize: 12, color: 'text.disabled'}}>{successShare.toFixed(1)}%</Typography>
                  </Stack>
                  <Stack direction='row' alignItems='center' spacing={1.25}>
                    <Box sx={{width: 10, height: 10, borderRadius: '3px', bgcolor: CORAL.main}} />
                    <Typography sx={{fontSize: 13, color: 'text.secondary', minWidth: 60}}>Failed</Typography>
                    <Typography sx={{fontSize: 15, fontWeight: 700}}>{failed.toLocaleString()}</Typography>
                    <Typography sx={{fontSize: 12, color: 'text.disabled'}}>{failedShare.toFixed(1)}%</Typography>
                  </Stack>
                </Stack>
              </Box>
            </Box>
          </Box>

          {/* KPI cards */}
          <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 2}}>
            <KpiCard label='Total Orders' value={total.toLocaleString()} icon={<Inventory2OutlinedIcon />} palette={INDIGO} sub='In selected window' />
            <KpiCard label='Success' value={success.toLocaleString()} icon={<CheckCircleOutlineIcon />} palette={TEAL} share={successShare} sub={`${successShare.toFixed(1)}% of total`} />
            <KpiCard label='Failed' value={failed.toLocaleString()} icon={<ErrorOutlineIcon />} palette={CORAL} share={failedShare} sub={`${failedShare.toFixed(1)}% of total`} />
            <KpiCard label='Failure Rate' value={rate.toFixed(2)} unit='%' icon={<SpeedOutlinedIcon />} palette={band.c} sub={band.label} />
          </Box>
        </>
      ) : (
        <LoadErrorPanel title='No data' message='No summary data for this window/scope.' />
      )}
    </>
  )
}
